import { EMPTY_STRING } from '@rotomeca/utils';
import { Nullable } from '@rotomeca/utils';
import { HTMLBnumBadge } from '../../atoms/badge/bnum-badge';
import { HTMLBnumButtonIcon } from '../../atoms/icon-button/bnum-icon-button';
import { HTMLBnumIcon } from '../../atoms/icon/bnum-icon';
import BnumElementInternal from '../../bnum-element-states';
import { HTMLBnumFolderList } from '../../organisms/folder-list/bnum-folder-list';
import style from './bnum-folder.less';
import { Define, Self } from '../../../core/decorators';
import { CustomDom } from '../../bnum-element';
import { TAG_FOLDER } from '../../../core/utils/constants/tags';
import { UI } from 'core/decorators/ui';
import * as CONST from './bnum-folder.internal/constants';
import { HTMLBnumFragment } from 'components/atoms';
import { Observe } from 'core/decorators/observe';
import { Schedule } from 'core/decorators/schedule';

/**
 * Structure de données pour l'événement personnalisé de changement de statut "non-lu".
 */
interface UnreadChangedEventDetail {
	unread: number;
	caller: HTMLBnumFolder;
}

/**
 * Interface représentant le cache des éléments du DOM interne (Shadow Root).
 * Permet un accès rapide sans requêter le DOM à chaque fois.
 */
declare type Ui = {
	name: HTMLLinkElement;
	icon: HTMLBnumIcon;
	toggle: HTMLBnumButtonIcon;
	badge: HTMLBnumBadge;
	container: HTMLElement;
};

//#region Template
const TEMPLATE = (
	<HTMLBnumFragment>
		<div class="bal-container">
			<div class="bal-container__title">
				<HTMLBnumIcon class="bal-container__title__icon">square</HTMLBnumIcon>
				<a tabindex="-1" id="bal-name" class="bal-container__title__name"></a>
			</div>
			<div class="bal-container__left">
				<HTMLBnumBadge circle class="bal-container__left__badge">
					0
				</HTMLBnumBadge>
				<HTMLBnumButtonIcon tabindex="-1" class="bal-container__toggle flex">
					keyboard_arrow_down
				</HTMLBnumButtonIcon>
			</div>
		</div>
		<HTMLBnumFolderList class="bal-sub-folders">
			<slot name="folders"></slot>
		</HTMLBnumFolderList>
	</HTMLBnumFragment>
);
//#endregion Template

/**
 * Composant Web Component représentant un dossier dans une structure arborescente.
 * Gère l'affichage hiérarchique, les badges de notification (non-lus), la sélection et l'état d'expansion.
 *
 * @structure Base
 * <bnum-folder
 * folder-id="identifiant-unique-du-dossier"
 * id="rcmliINBOX"
 * label="Dossier Racine"
 * unread="5"
 * icon="folder"
 * level="0"
 * is-virtual="false"
 * is-collapsed="true"
 * is-selected="false"
 * >
 * </bnum-folder>
 *
 * @structure Avec de sous-dossiers
 * <bnum-tree id="rcmliTREE">
 * <bnum-folder
 * folder-id="identifiant-unique-du-dossier"
 * id="rcmliINBOX"
 * label="Dossier Racine"
 * unread="17"
 * icon="folder"
 * level="0"
 * is-virtual="true"
 * is-collapsed="true"
 * is-selected="false"
 * >
 *  <bnum-folder
 *  slot="folders"
 *  folder-id="identifiant-unique-du-dossier-sub"
 *  id="rcmliSUBFOLDER"
 *  label="Dossier enfant"
 *  unread="17"
 *  icon="folder"
 *  level="1"
 *  is-virtual="false"
 *  is-collapsed="true"
 *  is-selected="false"
 *  >
 *  </bnum-folder>
 *  <bnum-folder
 *  slot="folders"
 *  folder-id="identifiant-unique-du-dossier-sub2"
 *  id="rcmliSUBFOLDER"
 *  label="Dossier enfant 2"
 *  unread="0"
 *  icon="folder"
 *  level="1"
 *  is-virtual="false"
 *  is-collapsed="true"
 *  is-selected="false"
 *  >
 *   <bnum-folder
 *   slot="folders"
 *   folder-id="identifiant-unique-du-dossier--sub-sub2"
 *   id="rcmliSUBFOLDERSUB"
 *   label="Dossier enfant enfant"
 *   unread="0"
 *   icon="folder"
 *   level="2"
 *   is-virtual="false"
 *   is-collapsed="true"
 *   is-selected="false"
 *   >
 *   </bnum-folder>
 *  </bnum-folder>
 * </bnum-folder>
 * </bnum-tree>
 *
 *
 * @slot folders - Slot pour insérer des sous-dossiers (`bnum-folder`).
 *
 * @state no-subfolders - Indique que le dossier n'a pas de sous-dossiers.
 * @state triple-digit-unread - Indique que le compteur de non-lu est à 3 chiffres (99+).
 * @state double-digit-unread - Indique que le compteur de non-lu est à 2 chiffres (10-99).
 * @state single-digit-unread - Indique que le compteur de non-lu est à 1 chiffre (1-9).
 *
 * @extends BnumElementInternal
 * @fires bnum-folder:unread-changed - Lorsqu'un compteur de non-lu est mis à jour.
 * @fires bnum-folder:select - Lorsque le dossier est sélectionné.
 * @fires bnum-folder:toggle - Lorsque le dossier est plié ou déplié.
 *
 * @attr {boolean} (default: true) is-collapsed - Indique si le dossier est visuellement replié.
 * @attr {boolean} (default: true) is-virtual - Indique si le dossier est virtuel.
 * @attr {boolean} (default: false) is-selected - Indique si le dossier est sélectionné.
 * @attr {number} (default: 0) unread - Nombre d'éléments non lus dans le dossier.
 * @attr {number} (default: 0) level - Niveau de profondeur du dossier dans l'arborescence.
 * @attr {string} (default: /) label - Libellé (nom) du dossier.
 * @attr {string} (default: /) icon - Nom de l'icône à afficher pour le dossier.
 *
 * @event {MouseEvent} click - Déclenché lorsque le dossier est cliqué.
 * @event {UnreadChangedEventDetail} bnum-folder:unread-changed - Événement custom pour le changement de non-lu
 * @event {CustomEvent<{ caller: HTMLBnumFolder; innerEvent?: Event }>} bnum-folder:select - Événement custom pour la sélection du dossier
 * @event {CustomEvent<{ caller: HTMLBnumFolder; innerEvent?: Event; collapsed: boolean }>} bnum-folder:toggle - Événement custom pour le pliage ou dépliage du dossier
 *
 * @cssvar {0.5em} --bnum-folder-indentation-base - Unité de base pour le calcul du décalage (padding-left) par niveau de profondeur.
 * @cssvar {0} --internal-bnum-folder-level - Variable interne (pilotée par JS) indiquant le niveau de profondeur actuel.
 * @cssvar {Calculated} --bnum-folder-indentation - Valeur finale du padding-left (base * level).
 * @cssvar {block} --bnum-folder-display - Type d'affichage du composant host.
 * @cssvar {100%} --bnum-folder-width - Largeur du composant host.
 * @cssvar {10px 15px} --bnum-folder-title-padding - Espacement interne du conteneur flex (Standard : 10px vertical, 15px horizontal).
 * @cssvar {10px} --bnum-folder-gap - Espace entre l'icône, le titre et les badges.
 * @cssvar {125px} --bnum-folder-text-ellipisis-max-width - Largeur maximale du libellé avant troncation.
 * @cssvar {inherit} --bnum-folder-icon-color - Couleur de l'icône du dossier.
 * @cssvar {5px} --bnum-badge-padding - Padding interne pour réduire la taille du badge (calcul de la taille).
 * @cssvar {#2e2eff} --bnum-color-primary-active - Couleur de fond du badge en mode cumulatif (Blue Thunder Active).
 * @cssvar {solid 1px #ddd} --bnum-border-in-column - Bordure inférieure appliquée aux dossiers de niveau 0.
 * @cssvar {15px 15px} --bnum-folder-bal-title-padding - Padding spécifique pour les dossiers racines (15px vertical, 15px horizontal).
 * @cssvar {#c1c1fb} --bnum-color-list-hover - Couleur de fond au survol d'un dossier interactif (Blue List Hover).
 * @cssvar {#e3e3fd} --bnum-color-list - Couleur de fond d'un dossier sélectionné (Blue List).
 * @cssvar {#adadf9} --bnum-color-list-drag - Couleur de fond lors du dragover (Blue List Active).
 */
@Define({ styles: style, template: TEMPLATE, tag: TAG_FOLDER })
@Observe(
	CONST.ATTR_LABEL,
	CONST.ATTR_UNREAD,
	CONST.ATTR_ICON,
	CONST.ATTR_IS_COLLAPSED,
	CONST.ATTR_LEVEL,
	CONST.ATTR_IS_SELECTED,
)
export class HTMLBnumFolder extends BnumElementInternal {
	//#region Private fields
	/**
	 * Compteur interne des éléments non lus propres à ce dossier (hors enfants).
	 * @private
	 * @type {number}
	 */
	#_selfUnread: number = 0;
	//#endregion Private fields

	//#region Getters/Setters
	/** Référence à la classe HTMLBnumFolder */
	@Self _!: typeof HTMLBnumFolder;

	/**
	 * Cache pour les éléments internes du Shadow DOM.
	 * Initialisé lors de `_p_buildDOM`.
	 * @private
	 * @type {Ui}
	 */
	@UI({
		name: `#${CONST.ID_NAME}`,
		icon: `.${CONST.CLASS_TITLE_ICON}`,
		toggle: `.${CONST.CLASS_TOGGLE}`,
		badge: `.${CONST.CLASS_LEFT_BADGE}`,
		container: `.${CONST.CLASS_CONTAINER}`,
	})
	accessor #_ui!: Ui;

	/**
	 * Indique si le dossier est visuellement replié.
	 * @returns {boolean} `true` si l'attribut `is-collapsed` est à 'true'.
	 */
	get collapsed(): boolean {
		return this.getAttribute(CONST.ATTR_IS_COLLAPSED) === CONST.VAL_TRUE;
	}

	/**
	 * Récupère la liste des classes CSS appliquées à l'élément hôte.
	 * @returns {string[]} Un tableau des classes.
	 */
	get classes(): string[] {
		return Array.from(this.classList);
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	/**
	 * Constructeur du composant.
	 */
	constructor() {
		super();
	}

	/**
	 * Construit le DOM et initialise les références UI et les écouteurs d'événements internes.
	 * @protected
	 * @param container - Le conteneur racine.
	 */
	protected _p_buildDOM(container: CustomDom): void {
		super._p_buildDOM(container);

		this.#_ui.container.addEventListener(CONST.EVENT_CLICK, (e: Event) => {
			this.select(e);
		});

		this.#_ui.toggle.addEventListener(CONST.EVENT_CLICK, (e: Event) => {
			this.toggle(e);
		});
	}

	/**
	 * Appelé lorsque le composant est attaché au DOM.
	 * Initialise les états par défaut et les écouteurs globaux.
	 * @protected
	 */
	protected _p_attach(): void {
		super._p_attach();

		if (this.hasAttribute(CONST.ATTR_IS_COLLAPSED) === false) {
			this.setAttribute(CONST.ATTR_IS_COLLAPSED, CONST.VAL_TRUE);
		}

		this.addEventListener(CONST.EVENT_SELECT, this.#_onFolderSelect.bind(this));

		// Initialisation des valeurs visuelles basées sur les attributs initiaux
		this.attr(CONST.ATTR_ROLE, CONST.VAL_ROLE_TREEITEM)
			.#_updateIcon(this.attr(CONST.ATTR_ICON) ?? EMPTY_STRING)
			.#_updateLabel(this.attr(CONST.ATTR_LABEL) ?? EMPTY_STRING)
			.#_updateLevel(
				this.attr(CONST.ATTR_LEVEL) ? +this.attr(CONST.ATTR_LEVEL)! : 0,
			)
			.#_updateSelected(this.attr(CONST.ATTR_IS_SELECTED) === CONST.VAL_TRUE)
			.#_updateIsCollapsed(
				this.attr(CONST.ATTR_IS_COLLAPSED) === CONST.VAL_TRUE,
			)
			.#_updateUnread(
				this.attr(CONST.ATTR_UNREAD)
					? +this.attr(CONST.ATTR_UNREAD)!
					: CONST.VAL_MIN_UNREAD,
			);
	}

	/**
	 * @inheritdoc
	 */
	protected _p_slotInit(slot: HTMLSlotElement): void {
		switch (slot.getAttribute('name')) {
			case 'folders':
				this._p_addState(CONST.STATE_NO_SUBFOLDERS);
				break;
			default:
				break;
		}
	}

	/**
	 * @inheritdoc
	 */
	protected _p_slotConnected(slot: HTMLSlotElement): void {
		switch (slot.getAttribute('name')) {
			case 'folders':
				if (slot.assignedElements().length > 0) {
					this._p_removeState(CONST.STATE_NO_SUBFOLDERS).addEventListener(
						CONST.EVENT_UNREAD_CHANGED,
						this.#_onChildUnreadChanged.bind(this),
					);
				}
				break;
			default:
				break;
		}
	}

	/**
	 * Gère la mise à jour des attributs observés.
	 * @protected
	 * @param {string} name - Nom de l'attribut modifié.
	 * @param {string | null} oldVal - Ancienne valeur.
	 * @param {string | null} newVal - Nouvelle valeur.
	 * @returns {void | Nullable<'break'>} Peut retourner 'break' pour arrêter la propagation.
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		if (name === CONST.ATTR_UNREAD) {
			// On gère les dissonances visuels (badge value vs attribute value)
			oldVal = this.#_ui.badge?.value ?? oldVal;

			// Optimisation: Evite les updates de DOM coûteux si déjà en 99+
			if (this.#_shouldSkipUnreadUpdate(oldVal, newVal)) return;
		}

		if (oldVal === newVal) return;

		switch (name) {
			case CONST.ATTR_LABEL:
				this.#_updateLabel(newVal ?? EMPTY_STRING);
				break;
			case CONST.ATTR_UNREAD:
				this.#_updateUnread(newVal ? +newVal : 0);
				break;

			case CONST.ATTR_ICON:
				this.#_updateIcon(newVal ?? EMPTY_STRING);
				break;

			case CONST.ATTR_IS_COLLAPSED:
				this.#_updateIsCollapsed(newVal === CONST.VAL_TRUE);
				this.#_refreshDisplay();
				break;

			case CONST.ATTR_LEVEL:
				this.#_updateLevel(newVal ? +newVal : 0);
				break;

			case CONST.ATTR_IS_SELECTED:
				this.#_updateSelected(newVal === CONST.VAL_TRUE);
				break;
			default:
				break;
		}
	}

	//#endregion Lifecycle

	//#region Event handlers
	/**
	 * Gestionnaire d'événement pour le changement de statut "non-lu" des enfants.
	 * Déclenche un rafraîchissement de l'affichage cumulatif si nécessaire.
	 * @private
	 * @param {Event} e - L'événement custom `UnreadChangedEventDetail`.
	 */
	#_onChildUnreadChanged(e: Event): void {
		const detail = (e as CustomEvent<UnreadChangedEventDetail>).detail;

		// Protection contre les boucles infinies (self-trigger)
		if (detail?.caller === this) return;

		this.#_refreshDisplay();
	}

	/**
	 * Intercepte la sélection pour empêcher l'action sur les dossiers virtuels.
	 * @private
	 * @param {Event} e - L'événement de sélection.
	 */
	#_onFolderSelect(e: Event): void {
		if (this.getAttribute(CONST.ATTR_IS_VIRTUAL) === CONST.VAL_TRUE) {
			e.stopPropagation();
		}
	}
	//#endregion Event handlers

	//#region Private methods
	/**
	 * Détermine si la mise à jour visuelle du badge doit être sautée (ex: 99+ vers 100).
	 * @private
	 * @param {string | null} oldVal - Ancienne valeur.
	 * @param {string | null} newVal - Nouvelle valeur.
	 * @returns {boolean} True si la mise à jour doit être ignorée.
	 */
	#_shouldSkipUnreadUpdate(
		oldVal: string | null,
		newVal: string | null,
	): boolean {
		const oldNum = oldVal ? +oldVal : CONST.VAL_MIN_UNREAD;
		const newNum = newVal ? +newVal : CONST.VAL_MIN_UNREAD;

		return oldNum > CONST.VAL_MAX_UNREAD && newNum > CONST.VAL_MAX_UNREAD;
	}

	/**
	 * Calcule le total des éléments non lus (Soi-même + tous les descendants).
	 * @private
	 * @returns {number} Le total calculé.
	 */
	#_getTotalUnread(): number {
		let total = this.#_selfUnread;
		const descendants = this.getElementsByTagName(this._.TAG);

		for (let i = 0, len = descendants.length; i < len; i++) {
			const val = descendants[i].getAttribute(CONST.ATTR_UNREAD);
			if (val) total += +val;
		}

		return total;
	}

	/**
	 * Met à jour uniquement l'élément visuel (Badge) en fonction de l'état (plié/déplié).
	 * Si plié, affiche le cumulatif. Si déplié, affiche le score propre.
	 * @private
	 */
	#_refreshDisplay(): void {
		if (!this.#_ui.badge) return;

		const isCollapsed = this.collapsed;
		const hasChildren = this.children.length > 0;

		const displayValue =
			isCollapsed && hasChildren ? this.#_getTotalUnread() : this.#_selfUnread;

		this.#_applyBadgeState(displayValue, isCollapsed);
	}

	/**
	 * Applique l'état visuel et la valeur au badge.
	 * @private
	 * @param {number} value - La valeur numérique à afficher.
	 * @param {boolean} isCollapsed - Si le dossier parent est replié (pour le style cumulatif).
	 */
	#_applyBadgeState(value: number, isCollapsed: boolean): void {
		const badge = this.#_ui.badge!;
		let state = CONST.STATE_NO_UNREAD;
		let text = EMPTY_STRING;

		if (value > CONST.VAL_MAX_UNREAD) {
			text = CONST.VAL_99_PLUS;
			state = CONST.STATE_TRIPLE_DIGIT;
		} else if (value > 0) {
			text = value.toString();
			state = value > 9 ? CONST.STATE_DOUBLE_DIGIT : CONST.STATE_SINGLE_DIGIT;
		}

		badge.forceValue(text);

		this._p_removeStates([
			CONST.STATE_NO_UNREAD,
			CONST.STATE_TRIPLE_DIGIT,
			CONST.STATE_DOUBLE_DIGIT,
			CONST.STATE_SINGLE_DIGIT,
		]);
		this._p_addState(state);

		const isCumulative = value !== this.#_selfUnread && isCollapsed;

		if (badge.classList.contains(CONST.CLASS_IS_CUMULATIVE) !== isCumulative) {
			badge.classList.toggle(CONST.CLASS_IS_CUMULATIVE, isCumulative);
		}
	}

	/**
	 * Met à jour le libellé du dossier dans le DOM.
	 * @private
	 * @param {string} label - Nouveau libellé.
	 * @returns {this}
	 */
	#_updateLabel(label: string): this {
		if (this.#_ui.name) {
			this.#_ui.name.textContent = label;
			this.#_ui.name.title = label;
		}
		return this;
	}

	/**
	 * Met à jour la valeur interne 'non-lu' et propage l'événement.
	 * @private
	 * @param {number} unread - Nouvelle valeur.
	 * @returns {this}
	 */
	#_updateUnread(unread: number): this {
		this.#_selfUnread = unread;
		this.#_refreshDisplay();

		if (this.alreadyLoaded) {
			this.trigger(
				CONST.EVENT_UNREAD_CHANGED,
				{
					unread: unread,
					caller: this,
				},
				{ bubbles: true, composed: true },
			);
		}
		return this;
	}

	/**
	 * Met à jour l'icône de toggle et l'attribut ARIA.
	 * @private
	 * @param {boolean} isCollapsed - État plié.
	 * @returns {this}
	 */
	#_updateIsCollapsed(isCollapsed: boolean): this {
		if (this.#_ui.toggle) {
			this.#_ui.toggle.icon = isCollapsed
				? CONST.ICON_ARROW_DOWN
				: CONST.ICON_ARROW_UP;
		}

		this.setAttribute(CONST.ARIA_EXPANDED, String(!isCollapsed));
		return this;
	}

	/**
	 * Met à jour l'icône principale du dossier.
	 * @private
	 * @param {string} icon - Nom de l'icône.
	 * @returns {this}
	 */
	#_updateIcon(icon: string): this {
		if (this.#_ui.icon) {
			this.#_ui.icon.icon = icon;
		}

		return this;
	}

	/**
	 * Met à jour le niveau d'indentation via CSS Variable.
	 * @private
	 * @param {number} level - Niveau de profondeur (clamped 0-10).
	 * @returns {this}
	 */
	#_updateLevel(level: number): this {
		const levelClamped = Math.max(0, Math.min(level, 10));
		this.style.setProperty(CONST.CSS_VAR_LEVEL, levelClamped.toString());
		return this;
	}

	/**
	 * Met à jour l'attribut ARIA de sélection.
	 * @private
	 * @param {boolean} isSelected - État sélectionné.
	 * @returns {this}
	 */
	#_updateSelected(isSelected: boolean): this {
		return this.attr(CONST.ARIA_SELECTED, isSelected.toString());
	}
	//#endregion Private methods

	//#region Public methods
	/**
	 * Bascule l'état plié/déplié du dossier.
	 * Met à jour l'attribut DOM et déclenche l'événement `EVENT_TOGGLE`.
	 * @public
	 * @param {Event} [innerEvent] - L'événement déclencheur originel (optionnel).
	 * @returns {this} L'instance courante pour chaînage.
	 */
	toggle(innerEvent?: Event): this {
		innerEvent?.stopPropagation?.();
		const isCollapsed =
			this.getAttribute(CONST.ATTR_IS_COLLAPSED) === CONST.VAL_TRUE;
		this.setAttribute(
			CONST.ATTR_IS_COLLAPSED,
			isCollapsed ? CONST.VAL_FALSE : CONST.VAL_TRUE,
		);

		this.trigger(CONST.EVENT_TOGGLE, {
			innerEvent,
			caller: this,
			collapsed: !isCollapsed,
		});

		return this;
	}

	/**
	 * Sélectionne le dossier.
	 * Déclenche l'événement `EVENT_SELECT`.
	 * @public
	 * @param {Event} [innerEvent] - L'événement déclencheur originel (optionnel).
	 * @returns {this} L'instance courante pour chaînage.
	 */
	select(innerEvent?: Event): this {
		this.trigger(CONST.EVENT_SELECT, {
			innerEvent,
			caller: this,
		});
		return this;
	}

	/**
	 * Force la maj des non-lus.
	 *
	 * La maj sera effectivement uniquement au prochain redraw.
	 * @param value Nouveau non-lus
	 */
	@Schedule()
	setUnreadValue(value: number) {
		this.#_updateUnread(value);
	}
	//#endregion Public methods

	//#region Static methods
	/**
	 * Génère la chaîne HTML statique pour ce composant (SSR / Helper).
	 * @static
	 * @param {Object} props - Propriétés de construction.
	 * @param {Record<string, string>} [props.attributes={}] - Attributs HTML.
	 * @param {string[]} [props.children=[]] - Contenu enfant.
	 * @returns {string} Le HTML sous forme de chaîne.
	 */
	static Write({
		attributes = {},
		children = [],
	}: {
		attributes?: Record<string, string>;
		children?: string[];
	} = {}): string {
		const childrenString = children.join(EMPTY_STRING);

		if (attributes && Object.keys(attributes).length > 0)
			return <HTMLBnumFolder {...attributes}>{childrenString}</HTMLBnumFolder>;
		else return <HTMLBnumFolder>{childrenString}</HTMLBnumFolder>;
	}

	//#region Static methods
}
